<template>
    <div class="event__edit_details">
        <form-text
            :name="$t('ebs.events.edit.name.name')"
            :label="$t('ebs.events.edit.labels.name')"
            :required="true"
            :v-validate="'required|max:200'"
            :data-vv-as="$t('forms.dataVvAs.name')"
            v-model="eventName"
        />
        <form-editor
            :name="'event-edit-description'"
            :label="$t('ebs.events.edit.name.description')"
            ref="formeditor"
            v-model="eventDescription"
        />
        <form-image
            :name="$t('ebs.events.edit.name.image')"
            :label="$t('ebs.events.edit.labels.eventImage')"
            :value="eventImage"
            @emit-select="eventSetImages"
            @emit-remove="eventRemoveImages"
        />
        <template v-if="isSecondStageTokenFromOldLMS">
            <form-selectlist
                :title="$t('ebs.events.edit.title.thisEventIsBeing')"
                :items="eventCourses"
                :select-items="courses"
                :select-value="eventSelectCourses"
                :allow-switch="false"
                :link-active="eventCourses.length === 0"
                :link-text="'Go to LMS and attach course to event'"
                @emit-link="openLMSCoursesRedirectNotify"
                @emit-remove="eventRemoveCourse"
                @emit-add="eventAddCourse"
            />
        </template>
        <form-select
            :label="$t('ebs.events.edit.labels.eventCoordinator')"
            :name="$t('ebs.events.edit.name.coordinator')"
            :value="eventCoordinator"
            :required="true"
            :is-type="'select'"
            :placeholder="$t('ebs.events.edit.labels.clickToSelect')"
            :searchable="false"
            v-validate="'required'"
            :multiple="true"
            :items-extra="[{
                label: loggedInUserName + loggedInUserId,
                customLabel: `${loggedInUserName} (${loggedInUserEmail})`,
                value: loggedInUserId
            }]"
            :get-items-getter="'getCoordinatorsMappedIdNameStatus'"
            :get-items-getter-mapped="'getCoordinatorsMappedLabelValue'"
            :get-items-dispatch="'getCoordinators'"
            :item-name="['coordinator', 'coordinators']"
            :search-queryable="false"
            :table-labels="[
                {
                    key: 'name',
                    title: 'Name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'email',
                    title: 'Email',
                    type: 'string',
                    sortable: true,
                    queryable: true
                }
            ]"
            @input="eventUpdateCoordinator"
            @input-table="eventUpdateCoordinator"
        />
        <form-checkbox
            :label="'Status'"
            :name="'status'"
            :switcher="true"
            :activeInactive="true"
            :disabled="eventHasBookings || (isSecondStageTokenFromOldLMS && (isAddMode || eventCourses.length === 0))"
            :value="eventStatus === 'active'"
            @input="updateEventStatus"
        >{{ isSecondStageTokenFromOldLMS && (isAddMode || eventCourses.length === 0) ? 'Field is disabled because event has to be created and assigned to course' : (eventHasBookings ? 'Visibility can be toggled if there are no learners booked onto this session.' : '') }}</form-checkbox>
        <form-submit
            :submit-button-animate-loading="!eventHasOccurrences"
            @reset="performReset"
            @submit="beforeEventSubmit"
        />
        <pop-notify
            ref="popnotifylmscoursesredirect"
            :class="'pop__notify--lmscoursesredirect'"
            :content-text="`You're going to leave the page. Do you want to save changes to this event?`"
            :custom-button-danger="true"
            :custom-button-active="true"
            :custom-button-text="`Don't save`"
            :prevent-close="true"
            :size="'medium'"
            :submit-button-text="'Confirm'"
            :title-text="'Save changes'"
            @emit-submit="eventSubmitRedirectToLMSCourses"
            @emit-custom="redirectToLMSCourses"
        />
        <pop-notify
            ref="popnotifynocourselmscoursesredirect"
            :class="'pop__notify--nocourselmscoursesredirect'"
            :title-text="'Changes saved'"
            :content-text="'Would you like to attach this event to a course?'"
            :cancel-button-text="'No'"
            :submit-button-text="'Yes'"
            :prevent-close="true"
            @emit-close="redirectToEventsList"
            @emit-submit="redirectToLMSCourses"
        />
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
        <pop-notify
            ref="popnotifyhasdraftsessions"
            :class="'pop__notify--hasdraftsessions'"
            :title-text="$t('ebs.events.edit.notify.titleDraftSession')"
            :content-text="$t('ebs.events.edit.notify.textDraftSession')"
            :cancel-button-text="$t('ebs.events.edit.notify.buttonClose')"
            :submit-button-active="false"
        />
        <pop-notify
            ref="popnotifynotaffectsession"
            :class="'pop__notify--notaffectsession'"
            :title-text="'Save changes?'"
            :content-text="'Your changes will not affect the existing sessions of this event.'"
            :cancel-button-text="$t('ebs.events.edit.notify.buttonClose')"
            :submit-button-text="$t('ebs.events.edit.notify.buttonConfirm')"
            :prevent-close="true"
            @emit-submit="performSubmitValidate"
        />
        <pop-notify
            ref="popnotifyeventdraftnotvisible"
            :class="'pop__notify--eventdraftnotvisible'"
            :title-text="'Inactive event'"
            :content-text="$t('ebs.events.edit.notify.textDraftEvent')"
            :cancel-button-text="$t('ebs.events.edit.notify.buttonClose')"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import FormText from '@/components/default/forms/FormText'
import FormImage from '@/components/default/forms/FormImage'
import FormSelect from '@/components/default/forms/FormSelect'
import FormSubmit from '@/components/default/forms/FormSubmit'
import PopNotify from '@/components/default/shared/PopNotify'
import FormSelectlist from '@/components/default/forms/FormSelectList'
import FormEditor from '@/components/default/forms/FormEditor'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import { performReset, performSubmitValidate, performSubmitValidatePromise, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'

export default {
  name: 'event-edit-details',
  components: {
    FormSelectlist,
    PopNotify,
    FormSubmit,
    FormSelect,
    FormImage,
    FormText,
    FormEditor,
    FormCheckbox
  },
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, performSubmitValidatePromise, watchContentChangeEmit],
  computed: {
    eventName: {
      get () {
        return this.$store.getters['getEventName']
      },
      set (value) {
        this.$store.dispatch('setEventName', value)
      }
    },
    eventDescription: {
      get () {
        return this.$store.getters['getEventDescription']
      },
      set (value) {
        this.$store.dispatch('setEventDescription', value)
      }
    },
    eventStatus: {
      get () {
        return this.$store.getters['getEventStatus']
      }
    },
    eventImage: {
      get () {
        return this.$store.getters['getEventImage']
      }
    },
    eventCourses: {
      get () {
        return this.$store.getters['getEventCourses']
      }
    },
    watchContent () {
      return this.eventName + this.eventDescription + this.eventStatus + this.eventImage + this.eventCourses + this.eventCoordinator
    },
    //
    rootItemName () {
      return 'event'
    },
    nameActionCreate () {
      return 'createEvent'
    },
    nameActionUpdate () {
      return 'updateEvent'
    },
    nameActionReset () {
      return 'resetEvent'
    },
    //
    loggedInUserId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataUserId
    },
    loggedInUserName () {
      return this.$store.getters.getAuthUserSecondStageTokenDataUserName
    },
    loggedInUserEmail () {
      return this.$store.getters.getAuthUserSecondStageTokenDataUserEmail
    },
    eventSelectCourses: function () {
      const courses = this.eventCourses
      let coursesId = []
      for (let i = 0; i < courses.length; i++) {
        coursesId[i] = courses[i].courseId
      }
      return coursesId
    },
    eventCoordinator: {
      get () {
        return this.$store.getters['getEventCoordinators']
      }
    },
    eventAllowEdit: function () {
      return this.$store.getters.getEventAllowEdit
    },
    eventAllowEditStatus: function () {
      return this.$store.getters.getEventAllowEditStatus
    },
    eventHasOccurrencesDraft () {
      return this.$store.getters.getEventHasOccurrencesDraft
    },
    eventHasBookings () {
      return this.$store.getters.getEventEventInfoHasBookings
    },
    eventHasOccurrences () {
      return this.$store.getters.getEventHasOccurrences
    },
    eventCustomRequestLoaded () {
      return this.$store.getters.getEventCustomRequestLoaded
    },
    isSecondStageTokenFromOldLMS () {
      return this.$store.getters.getAuthUserIsSecondStageTokenOldLMS
    },
    //
    courses: function () {
      const objectData = this.$store.getters['getEbsCourses']
      let objectDataArr = []
      if (objectData.length) {
        objectDataArr = objectData.map(function (object) {
          return {
            label: `${object.name}`,
            value: object.id
          }
        })
      }
      return objectDataArr
    },
    //
    isAddMode () {
      return this.$route.name.includes('-add')
    }
  },
  methods: {
    openLMSCoursesRedirectNotify () {
      this.$refs.popnotifylmscoursesredirect.openNotify()
    },
    openNoCourseLMSCoursesRedirectNotify () {
      this.$refs.popnotifynocourselmscoursesredirect.openNotify()
    },
    eventSubmitRedirectToLMSCourses () {
      this.performSubmitValidatePromise(false).then(() => {
        this.redirectToLMSCourses()
      }).finally(() => {
        this.$refs.popnotifylmscoursesredirect.closeNotify()
      })
    },
    redirectToLMSCourses () {
      const domain = this.$store.getters.getAuthUserSecondStageTokenDataDomain
      let LMSUrl = process.env.VUE_APP_LMS_SAAS_URL
      LMSUrl = LMSUrl.replace('[domain]', domain) + 'admin/multi_level_courses'
      location.replace(LMSUrl)
    },
    redirectToEventsList () {
      this.$router.push({ name: 'ebs-event-list' })
    },
    openNotifyEventDraftNotVisible () {
      this.$refs.popnotifyeventdraftnotvisible.openNotify()
    },
    closeNotifyEventDraftNotVisible () {
      this.$refs.popnotifyeventdraftnotvisible.closeNotify()
    },
    beforeEventSubmit () {
      if (this.isAddMode && this.isSecondStageTokenFromOldLMS) {
        if (this.eventCourses.length === 0) {
          this.performSubmitValidatePromise(false).then(() => {
            this.openNoCourseLMSCoursesRedirectNotify()
          })
        } else {
          this.performSubmitValidate()
        }
      } else {
        if (this.eventHasOccurrences) {
          this.openNotifyNotAffectSessions()
        } else {
          this.performSubmitValidate()
        }
      }
    },
    openNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.openNotify()
    },
    openNotifyHasDraftSessions () {
      this.$refs.popnotifyhasdraftsessions.openNotify()
    },
    closeNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.closeNotify()
    },
    closeNotifyHasDraftSessions () {
      this.$refs.popnotifyhasdraftsessions.closeNotify()
    },
    updateEventStatus (value) {
      let status = value ? 'active' : 'draft'
      this.$store.dispatch('setEventStatus', status)
      if (status === 'active' && this.eventHasOccurrencesDraft) {
        this.openNotifyHasDraftSessions()
      }
    },
    //
    getCoursesOldLMS: function () {
      this.$store.dispatch('ebsCoursesGetOldLMS')
    },
    //
    eventSetImages: function (data) {
      this.$store.dispatch('setEventImages', data)
    },
    eventRemoveImages: function (key) {
      this.$store.dispatch('removeEventImages', key)
    },
    eventUpdateCoordinator: function (data) {
      this.$store.dispatch('setEventCoordinators', data)
    },
    eventAddCourse: function (data) {
      this.$store.dispatch('setEventCourse', data)
    },
    eventRemoveCourse: function (key) {
      this.$store.dispatch('removeEventCourse', key)
    }
  },
  watch: {
    courses () {
      if (this.courses.length) {
        const lmsCourseId = this.$route.query.courseId
        if (lmsCourseId) {
          const lmsCourse = this.courses.filter(obj => {
            return obj.value === lmsCourseId
          })
          this.eventAddCourse(lmsCourse)
        }
      }
    },
    eventStatus (value) {
      if (value === 'active' && this.eventHasOccurrencesDraft) {
        this.openNotifyHasDraftSessions()
      } else if (value === 'draft') {
        this.openNotifyEventDraftNotVisible()
      }
    },
    eventCustomRequestLoaded (value) {
      if (value) {
        this.closeNotifyNotAffectSessions()
        this.closeNotifyHasDraftSessions()
      }
    }
  },
  created () {
    if (this.isSecondStageTokenFromOldLMS) {
      this.getCoursesOldLMS()
    }
  },
  mounted () {
    const userId = this.$store.getters.getAuthUserSecondStageTokenDataUserId
    if (userId && !this.eventCoordinator.length) {
      this.eventUpdateCoordinator([{ value: userId }])
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>
